import { useEffect } from 'react';

import { nanoid } from 'nanoid';
import { useFormContext } from 'react-hook-form';

import type { IQuotateSVCRes } from '@quick/go-api-types/quotations/create_quote';

import { TEXT_FIELD_ID } from '@/components/demandForm/constants';
import { useFetch } from '@/layers/core/deal/services/useFetch';

import { useFormDataContext } from './FormDataContext';
import type { IForm } from './types';
import useFormParams from './useFormParams';

export function FillFromQuote({ operations }: { operations?: boolean }) {
  const { reset, setValue } = useFormContext<IForm>();

  const [{ si: quote, reset: resetParam }] = useFormParams();

  const { data: formData } = useFormDataContext();
  const { data } = useFetch<IQuotateSVCRes>(
    !!quote && !resetParam && { url: `/quotations/${quote}`, auth: true }
  );

  useEffect(() => {
    // Prevent filling form when no data is available to render it
    if (!data) return;
    const user_id = data?.quotation.client_user.id;
    if (!formData)
      // avoid setting form user id if this is not done by an operations user from the services form
      return user_id && operations ? setValue('user_id', user_id) : undefined;
    const payload = data.quotation.payload;
    const serviceType = formData.lines
      .find(({ id }) => id === data.quotation.line.id)
      ?.service_types.find(({ id }) => id === data.quotation.service_type.id);
    const requiresVehicle = serviceType?.req_vehicle;
    const allAdditionals = serviceType?.additional_fields.filter(
      (af) => af.field.is_for_offer
    );
    const serviceAdditionals = allAdditionals?.reduce(
      (p, { field: { id, is_package } }) => {
        if (!is_package) p.add(id.toString());
        return p;
      },
      new Set<string>()
    );
    const packageFields = allAdditionals?.reduce(
      (p, { field: { id, is_package } }) => {
        if (is_package) p.add(id.toString());
        return p;
      },
      new Set<string>()
    );

    const line_id = data.quotation.line.id;

    const filled = {
      ...payload,
      line_id,
      // Prevent sending vehicle if configuration changed
      vehicle_type_id: requiresVehicle ? payload.vehicle_type_id : undefined,
      additional_data: {
        order_no: '',
        coupon: '',
        // Overwrite if fields present
        ...payload?.additional_data,
      },
      additional_fields: Object.entries(payload.additional_fields).reduce(
        (p, [k, v]) => {
          if (!serviceAdditionals?.has(k.toString())) return p;
          const key = `${k}_`;
          // Old Booleans
          if (typeof v === 'boolean') p[key] = v;
          // New booleans
          else {
            const first = v.at(0);
            if (!first) return p;
            // If config changes or back adds order, try to fill it
            // if (first.order !== undefined) return p;
            if ('goods' in first) p[key] = first.goods;
            else if ('text' in first) {
              p[`${key}${TEXT_FIELD_ID}`] = first.text;
            }
            // New Booleans
            else if ('value' in first) p[key] = first.value;
            // Prevent mutating query data
            else
              p[key] = {
                total: first.total,
                unit_type: first.unit_type,
                dimensions: { ...first.dimensions },
              };
          }
          return p;
        },
        {} as IForm['additional_fields']
      ),
      packages: Object.entries(payload.additional_fields).reduce(
        (p, [k, v]) => {
          if (!packageFields?.has(k.toString())) return p;
          const key = `${k}_`;
          // No boolean for packages
          if (typeof v === 'boolean') return p;
          // Package fields have an order param, unless backend adds them, or product messes with configuration

          // const filtered = (v as (AdditionalList | AdditionalMagnitude)[]) // Cast needed because weird error. Be careful when changing this
          //   .filter(({ order }) => order !== undefined);
          // if (!filtered.length) return p;

          v.forEach((field) => {
            const order = field.order ?? 0; // If it reached here, it has order field
            p[order] ??= { units: field.units, f: {} }; // Create object if array in position order does not exist
            if ('text' in field) {
              p[order].f[`${key}${TEXT_FIELD_ID}`] = field.text;
            } else {
              const val =
                'goods' in field
                  ? field.goods
                  : 'value' in field
                    ? field.value
                    : {
                        // Prevent mutating fetch response
                        dimensions: { ...field.dimensions },
                        unit_type: field.unit_type,
                        sub_total:
                          field.sub_total ?? field.total / (field.units || 1),
                        total: field.total,
                      };
              p[order].f[key] = val;
            }
          });
          return p;
        },
        [] as IForm['packages']
      ),
      dates: payload?.dates.map(({ date_time }) => ({
        date_time: date_time,
        uuid: nanoid(),
      })) ?? [{ date_time: '', uuid: nanoid() }],
      // TODO: Clean this to prevent data filled by back to get into the form
      stops: payload?.stops?.map((s) => ({ ...s, uuid: nanoid() })) ?? [],
      uom: payload?.uom ?? 'metric',
      user_id: operations ? data.quotation.client_user.id : undefined,
      // Remove fields added by back
      routes: undefined,
      manager_id: undefined,
      kms_amount: undefined,
      calculated_additional_fields: undefined,
      transport_conditions:
        payload?.additional_data?.transport_conditions ?? '',
      merchandise_description:
        payload.additional_data?.merchandise_description ?? '',
    };
    filled.merchandise_description ??= '';
    filled.transport_conditions ??= '';
    // Allow re-render. Solves weird bug
    const t = setTimeout(() => reset(filled), 0);
    return () => clearTimeout(t);
  }, [data, formData, operations, reset, setValue]);
  return null;
}
